import { makeAutoObservable, runInAction } from 'mobx'
import { generatePath } from 'react-router'
import { remoteStorageService } from 'src/utils/axios/remoteStorage'

import { guestController } from '@/controller/guest.controller'
import { studentController } from '@/controller/student.controller'
import type { GroupWorkbookPage, Student, StudentGroups } from '@/dto'
import { GroupInfo, SchoolGrade } from '@/dto'
import {
  getLessonStudentType,
  LESSON_STUDENT_TYPE,
  type LessonStudentType,
} from '@/page/Lesson/_components/lessonStudentType'
import { getEntranceTestStudentId } from '@/page/Lesson/Guest/_components/entranceTestSubTab'
import { guestsToStudentGroups } from '@/page/Lesson/Guest/_service/guestStudentGroups'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'

import { fetchLessonStudentGroups } from './fetchLessonStudentGroups'

/***
 * Lesson 페이지의 URL을 분석하여 Data로 변환하여 가지고 있는 서비스
 ***/
export class LessonGroupService {
  studentGroups?: StudentGroups

  groupLastPath = new Map<GroupInfo['key'], string>()
  guestStudentLastPath = new Map<Student['id'], string>()
  // 수업탭의 마지막 GroupInfo
  urlGroupInfo?: GroupInfo
  uiProblemScrollTop = new Map<GroupWorkbookPage['pageNum'], number>()

  constructor() {
    makeAutoObservable(this)
  }

  get groupKey() {
    return this.urlGroupInfo?.strKey ?? SchoolGrade.KO_LABEL[0]
  }

  get groupName() {
    return this.urlGroupInfo?.name ?? SchoolGrade.KO_LABEL[0]
  }

  get groupType() {
    return this.urlGroupInfo?.data.type ?? ''
  }

  get groupStudents() {
    if (!this.urlGroupInfo) return

    switch (this.urlGroupInfo.data.type) {
      case 'student': {
        const student = this.studentGroups?.getStudent(this.urlGroupInfo)
        if (!student) return
        return [student]
      }
      case 'grade':
      case 'lesson-class':
        return this.studentGroups?.getGroupFromGroupInfo(this.urlGroupInfo)?.students.toArr
    }
  }

  private _lastPathByStudentType: Partial<Record<LessonStudentType, string>> = {}

  // 모드를 경로에서 자체 도출해, 호출부가 다른 모드 슬롯을 오염시키는 실수를 차단한다.
  setLastPathByStudentType(fullPath: string) {
    if (!LessonGroupService.isLessonPage(fullPath)) return
    this._lastPathByStudentType[getLessonStudentType(fullPath)] = fullPath
  }

  getLastPathByStudentType(studentType: LessonStudentType) {
    return this._lastPathByStudentType[studentType]
  }

  // 폴백도 enrolled 경로일 때만 — guest 경로가 enrolled 타겟으로 새면 안 된다.
  get lessonLastPath() {
    const { router } = commonRepo
    const pathname = router?.location.pathname ?? ''
    return (
      this._lastPathByStudentType[LESSON_STUDENT_TYPE.enrolled] ??
      (LessonGroupService.isLessonPage(pathname) &&
        getLessonStudentType(pathname) === LESSON_STUDENT_TYPE.enrolled &&
        pathname)
    )
  }

  // Redirect 초기 경로
  get fallbackPath() {
    if (this.lessonLastPath) return this.lessonLastPath
    const pathData = LessonGroupService.getPathData(commonRepo.router.location.pathname)
    // 이전 URL에 group 정보가 있는 URL이라면 리다이렉트 안함
    if (pathData?.tab || pathData?.groupType || pathData?.key)
      return commonRepo.router.location.pathname

    // 첫번째 학년이 없을 시(재학중인 학생이 아무도 없을 때)
    if (!this.studentGroups?.firstGradeGroup) return RouteName.lesson

    const schoolGrade = SchoolGrade.fromKoLabel(this.studentGroups?.firstGradeGroup.id)
    // 학년 정보 만들기 실패시
    if (!schoolGrade) return RouteName.lesson

    if (!remoteStorageService.visited유형분석) {
      const groupInfo = new GroupInfo({
        type: 'student',
        name: this.studentGroups?.firstGradeGroup.students.toArr[0].name,
        studentId: this.studentGroups?.firstGradeGroup.students.toArr[0].id,
      })

      return `${RouteName.achievementAnalysis}/${groupInfo.data.type}/${groupInfo.key}`
    }

    const groupInfo = new GroupInfo({
      type: 'grade',
      schoolGrade,
    })

    return `${RouteName.lessonWorksheet}/${groupInfo.data.type}/${groupInfo.key}`
  }

  // 전환 시점 this.studentGroups 는 현재 모드 의존이라, 대상 모드의 명단 SSOT 에서 직접 첫 학생을
  // 구한다 — 안 그러면 반대 모드 첫 학생 id 가 대상 모드 URL 로 샌다. base 로만 보내면 fallbackPath
  // 단락 때문에 아무것도 선택되지 않는다.
  private firstStudentPath(groups: StudentGroups | undefined, route: string) {
    const firstStudent = groups?.firstGradeGroup?.students.toArr[0]
    if (!firstStudent) return

    return generatePath(route, { studentId: firstStudent.id })
  }

  get guestFallbackPath() {
    return this.firstStudentPath(
      guestsToStudentGroups(guestController.getCachedGuests() ?? []),
      RouteName.lessonGuestEntranceTestWorksheetStudent,
    )
  }

  // guest 캐시(getCachedGuests)는 콜드 진입·gcTime 수거로 비어 있을 수 있어, 그때 첫 학생을
  // 직접 fetch 한다. enrolled 와 대칭으로 두지 않으면 빈 캐시일 때 studentId 없는 base 로 떨어져
  // URL→선택 재동기화가 학생을 못 찾고 선택이 비어버린다.
  async getGuestFallbackPath() {
    return this.firstStudentPath(
      guestsToStudentGroups(await guestController.fetchGuests().catch(() => [])),
      RouteName.lessonGuestEntranceTestWorksheetStudent,
    )
  }

  async getEnrolledFallbackPath() {
    return this.firstStudentPath(
      await studentController.fetchStudentGroups(),
      RouteName.lessonWorksheetStudent,
    )
  }

  // 수업 URL 생성
  static makeGroupInfoPathName = ({
    groupInfo,
    baseUrl,
  }: {
    groupInfo?: GroupInfo
    baseUrl?: string
  }) => {
    const pathname = commonRepo.router.getLocationInfo().fullPath

    // 앞 4세그먼트(/{header}/{tab}/{groupType}/{key})를 새 baseUrl·groupInfo 로 치환한다.
    // 비재원생 경로는 6세그먼트(/lesson/guest/entrance-test/{tab}/student/{id})라, 뒤따르는
    // /student/{id} 를 함께 소비하지 않으면 치환 결과에 꼬리로 남아 .../student/{id}/student/{id}
    // 로 중복된다. 쿼리·해시는 보존해야 하므로 추가 세그먼트만(? # 직전까지) 소비한다.
    const newPathname = pathname.replace(
      /\/([^\\/]+)\/([^\\/]+)\/([^\\/]+)\/([^\\/]+)(?:\/[^\\/?#]+)*/,
      (full, $1, $2, groupType, groupKey) => {
        return `${baseUrl ? baseUrl : `/${$1}/${$2}`}/${groupInfo?.data.type ?? groupType}/${
          groupInfo?.key ?? groupKey
        }`
      },
    )

    return newPathname
  }

  // lesson 페이지인지
  static isLessonPage(pathname: string) {
    return !!pathname?.includes('/lesson/')
  }

  static getPathData(pathname: string) {
    if (LessonGroupService.isLessonPage(pathname)) {
      const match = pathname.match(/\/[^\\/]+\/([^\\/]+)\/([^\\/]+)\/([^\\/]+)/)
      if (match) {
        const [, tab, groupType, key] = match

        return {
          tab,
          groupType,
          key,
        }
      }
    }
  }

  // URL을 통해서 GroupInfo를 얻음
  static async getUrlGroupInfoFromPath(
    path: string,
    _pathData?: ReturnType<typeof LessonGroupService.getPathData>,
  ) {
    const studentGroups = await fetchLessonStudentGroups(path)

    // guest 학생 라우트는 /lesson/{tab}/{groupType}/{key} 세그먼트 구조가 아니어서 getPathData 로 파싱되지 않는다
    const guestStudentId = getEntranceTestStudentId(path)
    if (guestStudentId) {
      const student = studentGroups?.getStudent(guestStudentId)
      return student ? GroupInfo.fromStudent(student) : undefined
    }

    const pathData = _pathData ? _pathData : LessonGroupService.getPathData(path)
    if (!pathData?.tab || !pathData?.groupType || !pathData?.key) return

    const groupInfo = (() => {
      switch (pathData?.groupType) {
        case 'grade': {
          const schoolGrade = SchoolGrade.fromKoLabel(
            pathData.key as (typeof SchoolGrade.KO_LABEL)[number],
          )

          if (schoolGrade) {
            return new GroupInfo({
              type: 'grade',
              schoolGrade,
            })
          }
          break
        }
        case 'lesson-class': {
          const studentLessonClass = studentGroups?.studentsByLessonClass?.get(Number(pathData.key))

          if (studentLessonClass) {
            return new GroupInfo({
              type: 'lesson-class',
              classId: studentLessonClass.id,
              name: studentLessonClass.name ?? '',
            })
          }
          break
        }
        case 'student': {
          const student = studentGroups?.getStudent(pathData.key as Student['id'])
          if (student) {
            return new GroupInfo({
              type: 'student',
              studentId: student.id,
              name: student.name,
            })
          }
          break
        }
      }
    })()

    return groupInfo
  }

  setGroupInfo(groupInfo: GroupInfo) {
    this.urlGroupInfo = groupInfo
  }

  async fetchStudentGroups() {
    const pathname = commonRepo.router?.location.pathname ?? ''
    const studentGroups = await fetchLessonStudentGroups(pathname)
    runInAction(() => (this.studentGroups = studentGroups))
  }

  setLessonLastPath(fullPath: string) {
    this.setLastPathByStudentType(fullPath)
  }
}
